import { useMemo } from 'react';
import { useInputTableActions, usePasteConfig } from '@/components/custom/erp/input-table-v2';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { getAccountInfoTableColumns } from './columns';
import type { SelectedCellInfo } from '../hooks';
import { useTaiKhoanNganHang } from '@/hooks';
import { FormMode } from '@/types/form';

interface AccountInfoTabProps {
  formMode: FormMode;
  rows: { uuid: string }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: BanHangTableParams) => void;
  onCellClick?: (params: DataTableRenderCellParams<BanHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<BanHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

export function AccountInfoTab({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}: AccountInfoTabProps) {
  const { taiKhoanNganHangs } = useTaiKhoanNganHang();
  const columns = useMemo(
    () => getAccountInfoTableColumns(formMode, taiKhoanNganHangs, onCellValueChange),
    [formMode, taiKhoanNganHangs, onCellValueChange]
  );

  usePasteConfig({
    columns,
    onNonWritableFields: handleUpdateNonWritableFields,
    onPasteConfigs: handleUpdatePasteConfigs
  });
  const tableActions = useInputTableActions({
    onAddRow,
    onCopyRow,
    onDeleteRow,
    onMoveRow,
    onPasteRow
  });

  return (
    <ErpInputTableV2
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick as BanHangRecord}
      onCellClick={onCellClick as BanHangRecord}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns as BanHangRecord[]}
      getRowId={row => row?.uuid || ''}
      rowHeight={40}
      pasteTarget={selectedCell}
      actionLoading={isPasting}
      actionsDisabled={formMode === 'view'}
      actionOrder={['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin']}
      actions={tableActions}
    />
  );
}
